const LABEL_KEY = 'wortelogy-pos-resume-label'
const TOKEN_KEY = 'wortelogy-pos-resume-token'

export interface PosResumeStore {
  getItem(key: string): string | null
  setItem(key: string, value: string): void
  removeItem(key: string): void
}

export interface PosResumeLabel {
  uid: string
  name: string
  email: string
}

export interface PosResumeAccount extends PosResumeLabel {
  token: string
}

function browserStore(): PosResumeStore | null {
  try {
    if (typeof localStorage === 'undefined') return null
    return localStorage
  } catch {
    return null
  }
}

function readLabel(store: PosResumeStore): PosResumeLabel | null {
  try {
    const raw = store.getItem(LABEL_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<PosResumeLabel>
    const uid = parsed.uid?.trim() ?? ''
    const name = parsed.name?.trim() ?? ''
    const email = parsed.email?.trim() ?? ''
    if (!uid || !name || !email) return null
    return { uid, name, email }
  } catch {
    return null
  }
}

/** Saved account for the Continue button. Null until both the label and token exist. */
export function readPosResumeAccount(store: PosResumeStore | null = browserStore()): PosResumeAccount | null {
  if (!store) return null
  const label = readLabel(store)
  const token = store.getItem(TOKEN_KEY)?.trim() ?? ''
  if (!label || !token) return null
  return { ...label, token }
}

export function writePosResumeAccount(account: PosResumeAccount, store: PosResumeStore | null = browserStore()): void {
  if (!store) return
  const uid = account.uid.trim()
  const name = account.name.trim()
  const email = account.email.trim()
  const token = account.token.trim()
  if (!uid || !name || !email || !token) return
  store.setItem(LABEL_KEY, JSON.stringify({ uid, name, email }))
  store.setItem(TOKEN_KEY, token)
}

export function clearPosResumeAccount(store: PosResumeStore | null = browserStore()): void {
  if (!store) return
  store.removeItem(LABEL_KEY)
  store.removeItem(TOKEN_KEY)
}

/** Rejected or expired resume tokens drop the saved account. Network and undeployed callables do not. */
export function shouldClearSavedResume(code: string): boolean {
  const normalized = code.replace(/^(functions|auth)\//, '')
  return (
    normalized === 'permission-denied' ||
    normalized === 'failed-precondition' ||
    normalized === 'invalid-argument'
  )
}
